import React, { useEffect, useState } from 'react';
import { Link, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { FiSearch, FiArrowRight, FiArrowLeft, FiPlay, FiMessageCircle, FiFileText } from 'react-icons/fi';
import { toast } from '../Toast';
import useCarousel from '../useCarousel';
import { HELP_ICONS } from './helpIcons';
import { TOPICS, VIDEOS, POPULAR_SEARCHES, SUPPORT_EMAIL } from './helpContent';
import { searchHelp, topicResults, topicTitle, guidesInTopic, hrefForTitle, TOPIC_SLUGS, VIDEOS_TOPIC, HelpResult } from './helpGuides';
import HelpGuide from './HelpGuide';
import HelpVideo from './HelpVideo';

// /help — the full help centre ("Open full help centre" in the help panel), plus /help/guides/:slug and /help/videos/:slug.
// Content is static front-end content (helpContent.ts, helpGuides.ts). Search and topics are ?q= / ?topic= on /help.
const HelpCentre: React.FC = () => <Switch>
    <Route path="/help/guides/:slug" component={HelpGuide} />
    <Route path="/help/videos/:slug" component={HelpVideo} />
    <Route component={HelpHome} />
</Switch>;

const HelpHome: React.FC = () => {
    const history = useHistory();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const q = params.get('q') ?? '';
    const topic = params.get('topic') ?? '';
    const [search, setSearch] = useState(q);
    const videos = useCarousel<HTMLDivElement>();

    useEffect(() => setSearch(q), [q]);

    const runSearch = (text: string) => {
        const t = text.trim();
        history.push(t ? `/help?q=${encodeURIComponent(t)}` : '/help');
    };
    const allVideos = () => history.push(`/help?topic=${VIDEOS_TOPIC}`);

    const results: HelpResult[] | null = q ? searchHelp(q) : topic ? topicResults(topic) : null;
    const topicName = topic === VIDEOS_TOPIC ? 'Video guides' : topicTitle(topic);
    const topicInfo = TOPICS.find(t => t.title === topicName);

    return <div className="v2-page help-v2">
        <div className="hero">
            {/* same skyscraper photo as the login brand panel and the dashboard glance panel (img, not CSS url: see decisions.md 2026-09-18) */}
            <img className="hero-bg" src="/images/login/brand-panel.jpg" alt="" />
            <h1>How can we help you today?</h1>
            <p>Search our guides, watch short videos, or talk to a real person in Australian business hours.</p>
            <form className="search" onSubmit={e => { e.preventDefault(); runSearch(search); }}>
                <FiSearch />
                <input placeholder="Try “how do I email an announcement”" value={search} onChange={e => setSearch(e.target.value)} aria-label="Search help" />
                <button type="submit" className="btn-primary">Search</button>
            </form>
            <div className="popular">
                <span>Popular:</span>
                {POPULAR_SEARCHES.map(s => <button key={s} type="button" onClick={() => runSearch(s)}>{s}</button>)}
            </div>
        </div>

        {results !== null && <section className="card help-results">
            <div className="help-results-head">
                {topicInfo && <span className="icon-tile">{HELP_ICONS[topicInfo.icon]}</span>}
                {topic === VIDEOS_TOPIC && <span className="icon-tile"><FiPlay /></span>}
                <div>
                    <h2>{q ? `${results.length} ${results.length === 1 ? 'result' : 'results'} for “${q}”` : topicName ?? 'Topic not found'}</h2>
                    {!q && topicInfo && <p>{topicInfo.description}</p>}
                    {!q && topic === VIDEOS_TOPIC && <p>Short walk‑throughs with a written transcript. The videos themselves are coming soon.</p>}
                </div>
                <Link className="link" to="/help"><FiArrowLeft /> All topics</Link>
            </div>
            {results.map(r => <Link key={`${r.kind}-${r.slug}`} className="help-result" to={r.href}>
                <span className="icon-tile">{r.kind === 'video' ? <FiPlay /> : <FiFileText />}</span>
                <span className="help-result-text">
                    <strong>{r.title}</strong>
                    <small>{r.summary}</small>
                </span>
                <span className="help-result-meta">{r.kind === 'video' && <span className="pill">Video</span>}{q && <span className="pill">{r.topic}</span>}<span>{r.meta}</span></span>
                <FiArrowRight className="help-result-arrow" />
            </Link>)}
            {results.length === 0 && <div className="help-results-empty">
                <strong>No guides match{q ? ` “${q}”` : ''}</strong>
                <p>Try fewer or different words, browse the topics below, or email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> and we’ll help.</p>
            </div>}
        </section>}

        <h2 className="section-heading">Browse by topic</h2>
        <div className="topics">
            {TOPICS.map(t => {
                const count = guidesInTopic(t.title).length;
                return <div key={t.title} className="card topic">
                    <div className="topic-head">
                        <span className="icon-tile">{HELP_ICONS[t.icon]}</span>
                        <div><strong>{t.title}</strong><span>{count} {count === 1 ? 'guide' : 'guides'}</span></div>
                    </div>
                    <p>{t.description}</p>
                    <Link className="link" to={`/help?topic=${TOPIC_SLUGS[t.title]}`}>Open Guides <FiArrowRight /></Link>
                </div>;
            })}
        </div>

        <div className="help-row">
            <section className="card videos">
                <div className="section-title">
                    <span className="icon-tile"><FiPlay /></span>
                    <div><h2>Short Video Guides</h2><p>Each one is under 10 minutes, with a written transcript</p></div>
                    <button type="button" className="link in-title" onClick={allVideos}>All Videos <FiArrowRight /></button>
                </div>
                {/* grid on desktop / tablet; one-per-view carousel with dots on mobile (Help.scss) */}
                <div className="video-grid" ref={videos.ref} onScroll={videos.onScroll}>
                    {VIDEOS.map(v => <Link key={v.title} className="video" to={hrefForTitle(v.title)}>
                        <span className="thumb"><span className="play"><FiPlay /></span><span className="help-soon">Coming soon</span></span>
                        <strong>{v.title}</strong>
                        <small>{v.length} · Video · transcript available</small>
                    </Link>)}
                </div>
                {videos.count > 1 && <div className="carousel-dots">
                    {Array.from({ length: videos.count }, (_, i) => <button key={i} type="button" aria-label={`Show video ${i + 1}`}
                        className={i === videos.index ? 'active' : ''} onClick={() => videos.goTo(i)} />)}
                </div>}
                {/* mobile: the title-row link moves to the bottom of the card (Help.scss .in-title / .at-bottom) */}
                <button type="button" className="link at-bottom" onClick={allVideos}>All Videos <FiArrowRight /></button>
            </section>

            <section className="card contact">
                <div className="section-title">
                    <span className="icon-tile"><FiMessageCircle /></span>
                    <div><h2>Talk to a Person</h2><p>Mon–Fri, 8 am–6 pm AEST</p></div>
                </div>
                <button type="button" className="contact-row primary" onClick={() => toast.info('Preview only: live chat isn’t available yet')}>
                    <span className="text"><strong>Start a live chat</strong><small>Usually answered within 2 minutes</small></span><FiArrowRight />
                </button>
                <a className="contact-row" href={`mailto:${SUPPORT_EMAIL}`}>
                    <span className="text"><strong>Email {SUPPORT_EMAIL}</strong><small>We reply within one business day</small></span><FiArrowRight />
                </a>
                <button type="button" className="contact-row" onClick={() => toast.info('Preview only: call booking isn’t available yet')}>
                    <span className="text"><strong>Book a 30‑minute call</strong><small>Screen‑share with a specialist</small></span><FiArrowRight />
                </button>
                <div className="status"><span className="dot" /> All systems operating normally · status.investi.com.au</div>
            </section>
        </div>
    </div>;
};

export default HelpCentre;
